// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate` to regenerate.

// Read import.meta.env via a local cast rather than augmenting the global
// ImportMeta interface — that augmentation would conflict with vite/client's
// ImportMetaEnv declaration in any host app that pulls in both (TS2717).
const __env: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
/**
 * Auth runtime selector. Reads VITE_AUTH_PROVIDER at build time:
 *   entra     → Microsoft Entra ID / MSAL (SaaS-on-Azure)
 *   inferrex  → Inferrex JWT + MFA (sovereign/enterprise, default)
 *   dev       → Synthetic admin user, NO network (offline local dev only)
 *
 * Vite tree-shakes the unused implementation at build time, so the final
 * bundle only ships the active provider's code.
 *
 * SAFETY: the 'dev' value is ONLY honoured when import.meta.env.DEV is true
 * (i.e. `vite dev`/`vite preview`). A production build (`vite build` with
 * mode=production) ignores VITE_AUTH_PROVIDER=dev and falls back to inferrex
 * so a misconfigured deploy cannot accidentally ship an open admin panel.
 */

export type {
  AuthUser, AuthState, AuthContextValue, AuthProviderProps, SocialProvider,
} from './types.js';

import { InferrexAuthProvider, useInferrexAuth } from './inferrex-provider.js';
import { EntraAuthProvider, useEntraAuth } from './entra-provider.js';
import { DevBypassAuthProvider, useDevBypassAuth } from './dev-bypass-provider.js';
import type { AuthContextValue, AuthUser } from './types.js';

// Provider TYPE is build-time (VITE_AUTH_PROVIDER) on purpose: it's per deployment mode (SaaS→
// entra, sovereign→inferrex), and keeping it static lets Vite tree-shake the unused providers.
// Per-customer/per-domain variation is the provider CONFIG (clientId/tenantId/redirectUri,
// apiBaseUrl), which IS runtime via window.__INFERREX_CONFIG__ — see entra-provider + runtime-config.
const __raw_provider = (__env['VITE_AUTH_PROVIDER'] as string | undefined) ?? 'inferrex';
// vite sets MODE='development' for `vite dev` and 'production' for builds.
// MODE is canonically a string across vite versions; import.meta.env.DEV is a
// boolean and harder to compare safely once cast to Record<string, string>.
const __is_dev_build = __env['MODE'] === 'development';
const PROVIDER =
  __raw_provider === 'dev' && __is_dev_build  ? 'dev'
  : __raw_provider === 'entra'                ? 'entra'
  :                                              'inferrex';

if (__raw_provider === 'dev' && !__is_dev_build) {
  // Loud warning so a fat-fingered prod build is obvious in CI logs.
  console.error('[auth] VITE_AUTH_PROVIDER=dev ignored — not a dev build. Falling back to inferrex.');
}

export const AuthProvider =
  PROVIDER === 'dev'      ? DevBypassAuthProvider
  : PROVIDER === 'entra'    ? EntraAuthProvider
  :                           InferrexAuthProvider;

export function useAuth(): AuthContextValue {
  return PROVIDER === 'dev'      ? useDevBypassAuth()
       : PROVIDER === 'entra'    ? useEntraAuth()
       :                           useInferrexAuth();
}

export function useRequireAuth(): AuthContextValue & { user: AuthUser } {
  const auth = useAuth();
  if (!auth.user) throw new Error('useRequireAuth: user is not authenticated');
  return auth as AuthContextValue & { user: AuthUser };
}

export const ACTIVE_AUTH_PROVIDER = PROVIDER;

// Generated unauthenticated gate (replaces hand-written login pages).
export { AuthGate } from './auth-gate.js';
